import React, { FC, ReactElement } from 'react';
import { useApolloClient } from '@apollo/client';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import CollapsibleAttachments from '@buyside/components/presentation/IdeaShowPage/RightSidebar/Attachments/collapsibleAttachments';
import InfoTable from '@buyside/components/presentation/IdeaShowPage/InfoTable/infoTable';
import Link from '@buyside/components/presentation/Link/link';

import ChannelCheckIcon from '@buyside/svgs/channel_check.svg';
import FileTextIcon from '@buyside/svgs/document/file-text.svg';
import PaperStackIcon from '@buyside/svgs/paper_stack.svg';
import PDFIcon from '@buyside/svgs/document/file-pdf.svg';
import PenAndPaperIcon from '@buyside/svgs/pen_and_paper.svg';

import { ideaAttachmentUrl } from '@buyside/util/urlHelper';

import {
  IdeaAttachmentsQuery,
  IdeaCoreDocument,
  IdeaCoreQuery,
  IdeaCurrentUserDocument,
  IdeaCurrentUserQuery,
  useIdeaAttachmentsQuery
} from '@interfaces/graphql-typings';

export enum AttachmentTypes {
  SecondaryResearch = 'Secondary Research',
  ChannelCheck = 'Channel Check',
  FinancialModel = 'Financial Model'
}

import styles from './attachmentsStyles.scss';

const filterForResearchType = (
  attachments: IdeaAttachmentsQuery['idea']['ideaAttachments'],
  type: AttachmentTypes
): IdeaAttachmentsQuery['idea']['ideaAttachments'] =>
  attachments?.filter(attachment => attachment.researchType === type) || [];

const formatHeapTag = (string: string): string =>
  string
    .toLowerCase()
    .split(' ')
    .join('-');

const Attachments: FC<{
  ideaId: string;
  loading: boolean;
}> = ({ ideaId, loading: isLoading }) => {
  const client = useApolloClient();

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCoreDocument,
    variables: { ideaId }
  })?.idea;

  const cachedCurrentUser = client.readQuery<IdeaCurrentUserQuery>({
    query: IdeaCurrentUserDocument,
    variables: { ideaId }
  })?.currentUser;

  const { data, loading, error } = useIdeaAttachmentsQuery({
    variables: { ideaId }
  });

  if (isLoading || loading || !data?.idea || !cachedIdea) return null;
  if (error) throw error;

  const attachments = data.idea.ideaAttachments;
  const ideaPdfLink = cachedIdea.ideaPdfLink;

  const canDownloadIdeaPdf = Boolean(cachedCurrentUser?.canDownloadIdeaPdf);
  const canViewAttachments = Boolean(cachedIdea.canCurrentUserViewIdeaAttachments);

  const secondaryResearch = filterForResearchType(attachments, AttachmentTypes.SecondaryResearch);
  const channelCheck = filterForResearchType(attachments, AttachmentTypes.ChannelCheck);
  const financialModel = filterForResearchType(attachments, AttachmentTypes.FinancialModel);

  const formattedThesis = attachments?.find(
    attachment => attachment.researchType === 'Formatted Thesis'
  );

  return (
    <InfoTable
      dataTag="attachments"
      title="Attachments"
      isLocked={!canViewAttachments && !canDownloadIdeaPdf}
    >
      {(): ReactElement => (
        <div>
          {canDownloadIdeaPdf && ideaPdfLink ? (
            <div className={styles.itemContainer}>
              <Button
                url={ideaPdfLink}
                text={
                  <>
                    <span className={styles.pdfLink} data-heap="download-idea-pdf">
                      <PDFIcon className={styles.pdfIcon} />
                      Download Idea as a PDF
                    </span>
                  </>
                }
                size={BUTTON_SIZES.MEDIUM}
                type={BUTTON_TYPES.LINK}
              />
            </div>
          ) : null}
          {canViewAttachments && (
            <>
              {Boolean(secondaryResearch?.length) && (
                <CollapsibleAttachments
                  dataTag={formatHeapTag(AttachmentTypes.SecondaryResearch)}
                  files={secondaryResearch}
                  Icon={PaperStackIcon}
                  researchType={AttachmentTypes.SecondaryResearch}
                />
              )}
              {Boolean(channelCheck?.length) && (
                <CollapsibleAttachments
                  dataTag={formatHeapTag(AttachmentTypes.ChannelCheck)}
                  files={channelCheck}
                  Icon={ChannelCheckIcon}
                  researchType={AttachmentTypes.ChannelCheck}
                />
              )}
              {Boolean(financialModel?.length) && (
                <CollapsibleAttachments
                  dataTag={formatHeapTag(AttachmentTypes.FinancialModel)}
                  files={financialModel}
                  Icon={FileTextIcon}
                  researchType={AttachmentTypes.FinancialModel}
                />
              )}
              {formattedThesis && (
                <div className={styles.itemContainer}>
                  <Link to={ideaAttachmentUrl(formattedThesis.id)} target="_blank">
                    <PenAndPaperIcon className={styles.titleIcon} />
                    <span className={styles.header}>Download Formatted Report</span>
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </InfoTable>
  );
};

export default Attachments;
